"use client";

// OsSearch — the search affordance folded INTO the breadcrumb, available in EVERY
// space. The "search" crumb is a live input you type into right there; matches drop
// into an overlay anchored under the header, live as you type. It SCOPES to where
// you are: at the root it searches everything; inside a project (or a folder) it
// only returns results from that space. Clicking a hit navigates AND carries the
// query so the opened document highlights the match.
//
// On a phone, where a deep breadcrumb + a full input would overflow, the field
// collapses to a single magnifying-glass that expands on tap (the breadcrumb keeps
// its room); everywhere else it's just the word "search".

import { useEffect, useMemo, useRef, useState } from "react";

interface Hit {
  kind: "project" | "dimension" | "node" | "artifact";
  name: string;
  projectId: string;
  projectName: string;
  path: string;
  snippet: string;
}

const KIND_GLYPH: Record<Hit["kind"], string> = { project: "⬡", dimension: "◍", node: "✦", artifact: "❋" };

// bold the matched run inside a snippet/name so the eye lands on it.
function mark(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="os-find-mark">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export default function OsSearch({
  onNavigate,
  scopeProjectId,
  scopePath,
  scopeLabel,
  collapsible = false,
}: {
  onNavigate: (projectId: string, targetPath: string, query: string) => void;
  /** restrict results to this project (undefined at the root → search everything). */
  scopeProjectId?: string | null;
  /** restrict further to this path's subtree (a folder inside a project). */
  scopePath?: string | null;
  /** the space's name, shown in the placeholder ("search prowess"). */
  scopeLabel?: string | null;
  /** on a phone, collapse to a magnifying-glass until tapped (saves breadcrumb room). */
  collapsible?: boolean;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [mode, setMode] = useState<"lookup" | "ask">("lookup");
  const [loading, setLoading] = useState(false);
  // the last ask failed (offline, a 5xx, a stalled server): say so, never "nothing matches"
  const [failed, setFailed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false); // mobile: the field is expanded
  const wrapRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // reset the query whenever the space changes — a search is always about WHERE
  // you are, so it shouldn't leak across folders.
  useEffect(() => { setQ(""); setHits(null); setFailed(false); }, [scopeProjectId, scopePath]);

  // ONE ask is current at a time. Each run of the effect below retires the last
  // in its cleanup: its timer, its request (aborted) and its right to write, so a
  // slow answer to "ab" can never land over the answer to "abc" typed after it.
  useEffect(() => {
    if (q.trim().length < 2) {
      setHits(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    let live = true;
    const ac = new AbortController();
    // a stalled server is an answer too: after 8s it reads as a failure, not as "looking…"
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const wait = setTimeout(async () => {
      deadline = setTimeout(() => ac.abort(), 8000);
      try {
        const params = new URLSearchParams({ q });
        if (scopeProjectId) params.set("project", scopeProjectId);
        const res = await fetch(`/api/os/search?${params.toString()}`, { signal: ac.signal });
        if (!res.ok) throw new Error(`search ${res.status}`);
        const data = await res.json();
        if (!live) return; // a newer ask owns the results now
        setHits(Array.isArray(data?.results) ? (data.results as Hit[]) : []);
        setMode(data?.mode === "ask" ? "ask" : "lookup");
        setFailed(false);
      } catch {
        if (!live) return;
        setHits([]);
        setFailed(true);
      } finally {
        if (deadline) clearTimeout(deadline);
        if (live) setLoading(false);
      }
    }, 240);
    return () => {
      live = false;
      clearTimeout(wait);
      if (deadline) clearTimeout(deadline);
      ac.abort();
    };
  }, [q, scopeProjectId]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setFocused(false); setOpen(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // SCOPE the results to where you are: same project, and (if you're deeper than a
  // project root) the current folder's subtree. At the root nothing is scoped.
  const scoped = useMemo(() => {
    let h = hits ?? [];
    if (scopeProjectId) h = h.filter((x) => x.projectId === scopeProjectId);
    if (scopePath && scopeProjectId && scopePath !== `/projects/${scopeProjectId}`) {
      h = h.filter((x) => x.path === scopePath || x.path.startsWith(scopePath + "/"));
    }
    return h;
  }, [hits, scopeProjectId, scopePath]);

  const show = focused && q.trim().length >= 2 && hits !== null;
  // the field always just says "search" — the SCOPE is communicated in the results
  // overlay ("in {space} · live"), not crammed into the placeholder.
  const placeholder = "search";
  const expanded = !collapsible || open || focused || q.length > 0;

  return (
    <span
      ref={wrapRef}
      data-noswipe
      style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", position: "relative", minWidth: 0 }}
    >
      <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
      {collapsible && !expanded ? (
        <button
          type="button"
          className="os-crumb-search-btn"
          aria-label={placeholder}
          data-hint-key="nav.search"
          onClick={() => { setOpen(true); setFocused(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
        >
          {/* the collapsed phone glyph — slightly smaller, and drawn so the CIRCLE
              (not the whole glyph) sits optically centred against the crumb text. */}
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none" aria-hidden style={{ display: "block", transform: "translateY(-0.5px)" }}>
            <circle cx="7" cy="7.4" r="4.4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.4 10.8 14 14.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <input
          ref={inputRef}
          className="os-crumb-search"
          placeholder={placeholder}
          value={q}
          size={Math.max(placeholder.length, q.length + 1)}
          data-hint-key="nav.search"
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setQ("");
              (e.target as HTMLInputElement).blur();
              setOpen(false);
            }
            e.stopPropagation();
          }}
          aria-label={scopeLabel ? `search inside ${scopeLabel}` : "search your innernet"}
        />
      )}
      {show && (
        <div className="os-search-results" style={{ left: 0, right: "auto", width: "min(34rem, 80vw)", top: "calc(100% + 0.7rem)" }}>
          {scopeLabel && (
            <div style={{ fontSize: "0.72rem", color: "var(--ink-4)", padding: "0.1rem 0.2rem 0.5rem" }}>
              in {scopeLabel} · live
            </div>
          )}
          {mode === "ask" && (
            <div style={{ fontSize: "0.74rem", color: "var(--accent-ink)", padding: "0.2rem 0.2rem 0.6rem" }}>
              innernet is finding this for you · {scoped.length} {scoped.length === 1 ? "result" : "results"}
            </div>
          )}
          {loading && scoped.length === 0 && <div style={{ color: "var(--ink-4)", fontSize: "0.85rem", padding: "0.4rem" }}>looking…</div>}
          {!loading && scoped.length === 0 && (
            <div style={{ color: "var(--ink-4)", fontSize: "0.85rem", padding: "0.4rem" }}>
              {failed ? "couldn't search just now. please try again." : `nothing matches${scopeLabel ? ` in ${scopeLabel}` : ""} yet.`}
            </div>
          )}
          {scoped.map((h, i) => (
            <button
              key={i}
              className="os-search-hit"
              onClick={() => {
                onNavigate(h.projectId, h.path, q.trim());
                setFocused(false);
                setOpen(false);
                setQ("");
              }}
            >
              <span style={{ color: "var(--accent-ink)", width: "1.4rem", flexShrink: 0 }}>{KIND_GLYPH[h.kind]}</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ color: "var(--ink)", fontSize: "0.95rem" }}>
                  {mark(h.name, q.trim())} <span style={{ color: "var(--ink-4)", fontSize: "0.78rem" }}>· {h.projectName}</span>
                </span>
                {h.snippet && (
                  <span style={{ color: "var(--ink-3)", fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mark(h.snippet, q.trim())}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
